import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import MobileFilterRail from "@/components/MobileFilterRail";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, ShieldCheck, X, Search, ExternalLink, ChevronLeft,
} from "lucide-react";

const STATUS_META = {
  open:        { label: "مفتوحة",     cls: "bg-rose-50 text-rose-700 border-rose-200" },
  in_progress: { label: "قيد المعالجة", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  resolved:    { label: "تم حلّها",    cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  dismissed:   { label: "مرفوضة",      cls: "bg-slate-50 text-slate-600 border-slate-200" },
};

const PRIORITY_META = {
  low:    { label: "عادية",   cls: "bg-slate-50 text-slate-700 border-slate-200" },
  medium: { label: "متوسطة", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  high:   { label: "عالية",   cls: "bg-rose-50 text-rose-700 border-rose-200" },
};

const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("ar-SA"); } catch { return "—"; } };

export default function AdminComplaints() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all|open|in_progress|resolved|dismissed
  const [q, setQ] = useState("");
  const [modalFor, setModalFor] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/complaints");
      setItems(data.complaints || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return items.filter((c) => {
      if (filter !== "all" && c.status !== filter) return false;
      if (!term) return true;
      return [c.student_name, c.subject, c.body, c.filed_by_name].some((f) => (f || "").toLowerCase().includes(term));
    });
  }, [items, filter, q]);

  const counts = useMemo(() => ({
    all: items.length,
    open: items.filter((c) => c.status === "open").length,
    in_progress: items.filter((c) => c.status === "in_progress").length,
    resolved: items.filter((c) => c.status === "resolved").length,
    dismissed: items.filter((c) => c.status === "dismissed").length,
  }), [items]);

  return (
    <DashboardLayout>
      <div data-testid="admin-complaints">
        <div className="mb-6">
          <div className="text-xs font-bold uppercase tracking-widest text-teal-700">لوحة الإدارة</div>
          <h1 className="mt-2 text-2xl font-heading font-black text-slate-900">شكاوى الطلاب</h1>
          <p className="mt-2 text-slate-600 max-w-3xl">
            صفحة موحّدة لكل الشكاوى القادمة من الجمعيات والموجّهين وأعضاء هيئة التدريس. وجّهها، تابع حالتها، وأغلقها بلمسة واحدة.
          </p>
        </div>

        <div className="hidden md:grid md:grid-cols-5 gap-3 mb-6" data-testid="complaints-kpi">
          <Kpi label="الكل" value={counts.all} active={filter === "all"} onClick={() => setFilter("all")} tint="bg-teal-50 text-teal-800 border-teal-100" />
          <Kpi label="مفتوحة" value={counts.open} active={filter === "open"} onClick={() => setFilter("open")} tint="bg-rose-50 text-rose-800 border-rose-100" />
          <Kpi label="قيد المعالجة" value={counts.in_progress} active={filter === "in_progress"} onClick={() => setFilter("in_progress")} tint="bg-amber-50 text-amber-800 border-amber-100" />
          <Kpi label="محلولة" value={counts.resolved} active={filter === "resolved"} onClick={() => setFilter("resolved")} tint="bg-emerald-50 text-emerald-800 border-emerald-100" />
          <Kpi label="مرفوضة" value={counts.dismissed} active={filter === "dismissed"} onClick={() => setFilter("dismissed")} tint="bg-slate-50 text-slate-700 border-slate-200" />
        </div>

        <MobileFilterRail
          value={filter === "all" ? "" : filter}
          onChange={(id) => setFilter(id || "all")}
          filters={[
            { id: "", label: "الكل", count: counts.all },
            { id: "open", label: "مفتوحة", count: counts.open },
            { id: "in_progress", label: "قيد المعالجة", count: counts.in_progress },
            { id: "resolved", label: "محلولة", count: counts.resolved },
            { id: "dismissed", label: "مرفوضة", count: counts.dismissed },
          ]}
        />

        <div className="mb-5 p-3 bg-white rounded-2xl border border-cream-200 card-shadow flex items-center gap-2">
          <Search className="h-4 w-4 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="بحث بالطالب، الموضوع، أو رافع الشكوى" className="flex-1 bg-transparent text-sm focus:outline-none" data-testid="complaints-search" />
        </div>

        {loading ? (
          <div className="grid place-items-center py-16"><Loader2 className="h-8 w-8 animate-spin text-teal-700" /></div>
        ) : filtered.length === 0 ? (
          <div className="p-5 rounded-2xl border border-dashed border-slate-300 bg-white text-center text-slate-500">
            {items.length === 0 ? "لا توجد شكاوى بعد." : "لا نتائج مطابقة."}
          </div>
        ) : (
          <>
            {/* Mobile card list */}
            <div className="md:hidden space-y-3" data-testid="complaints-cards">
              {filtered.map((c) => {
                const s = STATUS_META[c.status] || STATUS_META.open;
                const p = PRIORITY_META[c.priority] || PRIORITY_META.medium;
                return (
                  <article key={c.id} className="bg-white rounded-2xl border border-cream-200 p-4 card-shadow" data-testid={`row-${c.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="font-black text-slate-900 truncate">{c.student_name}</div>
                        <div className="mt-0.5 text-xs text-slate-500 arabic-numerals">{fmtDate(c.created_at)}</div>
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${s.cls}`}>{s.label}</span>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${p.cls}`}>{p.label}</span>
                      </div>
                    </div>
                    <div className="mt-2 text-sm font-semibold text-slate-800 line-clamp-2">{c.subject}</div>
                    <div className="mt-2 text-xs text-slate-500">
                      رافع الشكوى: <b className="text-slate-700">{c.filed_by_name}</b> ({c.filed_by_role})
                    </div>
                    <button onClick={() => setModalFor(c)} className="mt-3 inline-flex items-center gap-1 min-h-11 px-4 rounded-xl bg-teal-700 text-white text-xs font-bold hover:bg-teal-600 w-full justify-center" data-testid={`open-${c.id}`}>
                      فتح تفاصيل الشكوى <ChevronLeft className="h-3.5 w-3.5" />
                    </button>
                  </article>
                );
              })}
            </div>
            {/* Desktop table */}
            <div className="hidden md:block overflow-hidden rounded-2xl bg-white border border-cream-200 card-shadow" data-testid="complaints-table">
              <table className="w-full text-sm">
                <thead className="bg-cream-50">
                  <tr className="text-slate-600">
                    <Th>الطالب</Th><Th>الموضوع</Th><Th>الأولوية</Th><Th>الحالة</Th><Th>رافع الشكوى</Th><Th>تاريخ</Th><Th></Th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((c) => {
                    const s = STATUS_META[c.status] || STATUS_META.open;
                    const p = PRIORITY_META[c.priority] || PRIORITY_META.medium;
                    return (
                      <tr key={c.id} className="border-t border-cream-100 hover:bg-cream-50/70" data-testid={`row-desktop-${c.id}`}>
                        <td className="px-4 py-3 font-semibold text-slate-900">{c.student_name}</td>
                        <td className="px-4 py-3 text-slate-700 max-w-xs truncate">{c.subject}</td>
                        <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-[11px] font-bold border ${p.cls}`}>{p.label}</span></td>
                        <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-[11px] font-bold border ${s.cls}`}>{s.label}</span></td>
                        <td className="px-4 py-3 text-slate-700">{c.filed_by_name} <span className="text-xs text-slate-400">({c.filed_by_role})</span></td>
                        <td className="px-4 py-3 text-slate-500 arabic-numerals">{fmtDate(c.created_at)}</td>
                        <td className="px-4 py-3 text-end">
                          <button onClick={() => setModalFor(c)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-teal-700 text-white text-xs font-bold hover:bg-teal-600" data-testid={`open-${c.id}`}>
                            فتح
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        {modalFor && <ComplaintModal complaint={modalFor} onClose={() => setModalFor(null)} onChanged={() => { setModalFor(null); load(); }} />}
      </div>
    </DashboardLayout>
  );
}

function Th({ children }) {
  return <th className="text-start px-4 py-3 font-semibold text-xs uppercase tracking-widest">{children}</th>;
}

function Kpi({ label, value, active, onClick, tint }) {
  return (
    <button
      onClick={onClick}
      className={`p-3 rounded-2xl border transition-all text-start ${tint} ${active ? "ring-2 ring-teal-500 -translate-y-0.5 shadow" : "opacity-90 hover:opacity-100"}`}
      data-testid={`kpi-${label}`}
    >
      <div className="text-[11px] font-bold uppercase tracking-widest">{label}</div>
      <div className="text-2xl font-heading font-black arabic-numerals">{value}</div>
    </button>
  );
}

function ComplaintModal({ complaint, onClose, onChanged }) {
  const [note, setNote] = useState(complaint.resolution_note || "");
  const [busy, setBusy] = useState(false);

  const patch = async (fields) => {
    setBusy(true);
    try {
      await api.patch(`/complaints/${complaint.id}`, fields);
      toast.success("تم التحديث");
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const s = STATUS_META[complaint.status] || STATUS_META.open;
  const p = PRIORITY_META[complaint.priority] || PRIORITY_META.medium;
  const closed = ["resolved", "dismissed"].includes(complaint.status);

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/60 backdrop-blur-sm p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-slate-200 max-h-[92vh] overflow-y-auto" data-testid="complaint-modal">
        <div className="sticky top-0 z-10 px-4 py-3 bg-gradient-to-l from-teal-700 to-teal-800 text-white flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-xs opacity-80">شكوى بخصوص</div>
            <div className="mt-0.5 font-heading font-black text-lg truncate">{complaint.student_name}</div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-4 space-y-4">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold border ${p.cls}`}>الأولوية: {p.label}</span>
            <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold border ${s.cls}`}>{s.label}</span>
          </div>
          <div>
            <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">الموضوع</div>
            <div className="font-heading font-bold text-slate-900">{complaint.subject}</div>
          </div>
          <div>
            <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">التفاصيل</div>
            <div className="text-sm text-slate-800 whitespace-pre-line">{complaint.body}</div>
          </div>
          <div className="text-xs text-slate-500 flex items-center gap-2">
            رفعها: <span className="font-bold text-teal-700">{complaint.filed_by_name}</span> ({complaint.filed_by_role}) — {fmtDate(complaint.created_at)}
            <Link to={`/dashboard/mentor/student/${complaint.student_id}`} className="ms-auto inline-flex items-center gap-1 text-teal-700 hover:text-teal-800 font-semibold">
              فتح ملف الطالب <ExternalLink className="h-3 w-3" />
            </Link>
          </div>

          {complaint.resolution_note && (
            <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-100 text-sm">
              <div className="text-[11px] font-bold text-emerald-700 mb-1">ملاحظة الإغلاق الحالية</div>
              <div className="text-slate-800 whitespace-pre-line">{complaint.resolution_note}</div>
            </div>
          )}

          {!closed && (
            <>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">ملاحظة الإغلاق</label>
                <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="اشرح كيف تمّت المعالجة..." data-testid="admin-note" />
              </div>
            </>
          )}
        </div>
        <div className="sticky bottom-0 px-4 py-3 bg-white border-t border-slate-100 flex flex-wrap gap-2 justify-end">
          {!closed && complaint.status === "open" && (
            <button disabled={busy} onClick={() => patch({ status: "in_progress" })} className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-amber-500 text-white text-sm font-bold hover:bg-amber-600 disabled:opacity-60" data-testid="admin-start">
              بدء المعالجة
            </button>
          )}
          {!closed && (
            <>
              <button disabled={busy} onClick={() => patch({ status: "dismissed", resolution_note: note })} className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-slate-500 text-white text-sm font-bold hover:bg-slate-600 disabled:opacity-60" data-testid="admin-dismiss">
                رفض
              </button>
              <button disabled={busy} onClick={() => patch({ status: "resolved", resolution_note: note })} className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 disabled:opacity-60" data-testid="admin-resolve">
                <ShieldCheck className="h-4 w-4" /> تم الحل
              </button>
            </>
          )}
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-slate-700 hover:bg-slate-100 text-sm">إغلاق</button>
        </div>
      </div>
    </div>
  );
}
